// src/sectors/pilates/staff/modal/staffLoginModal/LoginEditModal.jsx
import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';

import { ConfirmLoginStatusModal } from './ConfirmLoginStatusModal';

const empty = () => ({
  username: '',
  password: '',
});

export function LoginEditModal({ open, staff, onClose, onSubmit, onDisableLogin }) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [form, setForm] = React.useState(empty());
  const [err, setErr] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  // ✅ artık disableOpen değil
  const [statusModalOpen, setStatusModalOpen] = React.useState(false);

  const hasAccount = Boolean(staff?.hasLoginAccount || staff?.username);

  // ✅ loginStatus backend’den gelmiyorsa username var diye ACTIVE varsayalım
  const loginStatus = staff?.loginStatus || (hasAccount ? 'ACTIVE' : null);
  const isSuspended = loginStatus === 'SUSPENDED';

  React.useEffect(() => {
    if (!open) return;
    setErr(null);
    setLoading(false);
    setStatusModalOpen(false);
    setForm({
      username: staff?.username || '',
      password: '',
    });
  }, [open, staff]);

  if (!open) return null;

  const canSubmit = String(form.username || '').trim().length >= 3;
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    setErr(null);

    const username = String(form.username || '').trim();

    if (!username) return setErr('Kullanıcı adı boş olamaz.');
    if (username.length < 3) return setErr('Kullanıcı adı en az 3 karakter olmalı.');

    if (String(form.password || '').length > 0 && String(form.password || '').length < 6) {
      return setErr('Şifre giriyorsan en az 6 karakter olmalı.');
    }

    const body = {
      username,
      ...(String(form.password || '').length > 0 ? { password: String(form.password || '') } : {}),
    };

    try {
      setLoading(true);
      await onSubmit?.(body);
      onClose?.();
    } catch (e) {
      setErr(e?.message || 'Giriş bilgileri güncellenemedi.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ ConfirmLoginStatusModal burayı çağıracak
  const confirmLoginStatusChange = async (nextStatus) => {
    setErr(null);

    if (!staff?.id) return setErr('Staff bilgisi eksik (id yok).');
    if (!onDisableLogin) return setErr("onDisableLogin bağlı değil (parent'ta prop verilmemiş).");

    try {
      console.log('🔴 [LoginEditModal.confirmLoginStatusChange]', {
        staffId: staff.id,
        nextStatus,
      });
      setLoading(true);

      // ✅ parent fonksiyonu request atacak
      await onDisableLogin(staff, nextStatus);

      console.log('✅ [LoginEditModal.confirmLoginStatusChange] OK');
      setStatusModalOpen(false);
      onClose?.();
    } catch (e) {
      console.error('❌ [LoginEditModal.confirmLoginStatusChange] error:', e);
      setErr(e?.message || 'İşlem başarısız.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? undefined : onClose}
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '22px',
            border: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
            boxShadow: theme.shadows[12],
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            background: 'linear-gradient(180deg, rgba(32,156,238,.16), rgba(32,156,238,.06))',
          }}
        >
          <MDBox sx={{ px: 2, py: 1.6 }}>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <MDBox display="flex" alignItems="center" gap={1}>
                <MDBox
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '16px',
                    display: 'grid',
                    placeItems: 'center',
                    background: 'rgba(32,156,238,.14)',
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Icon sx={{ fontSize: 22 }}>manage_accounts</Icon>
                </MDBox>

                <MDBox>
                  <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                    Giriş Bilgilerini Düzenle
                  </MDTypography>
                  <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                    {staff?.fullName
                      ? `${staff.fullName} için kullanıcı adı / şifre`
                      : 'Kullanıcı adı / şifre'}
                  </MDTypography>
                </MDBox>
              </MDBox>

              <MDButton
                variant="outlined"
                color="dark"
                onClick={onClose}
                disabled={loading}
                sx={{ borderRadius: '14px' }}
              >
                Kapat
              </MDButton>
            </MDBox>
          </MDBox>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 2 }}>
          <MDBox sx={{ display: 'grid', gap: 1.25 }}>
            <MDInput
              label="Kullanıcı adı"
              value={form.username}
              onChange={(e) => set('username', e.target.value)}
              helperText="En az 3 karakter"
            />

            <MDInput
              label="Yeni şifre (opsiyonel)"
              type="password"
              value={form.password}
              onChange={(e) => set('password', e.target.value)}
              helperText="Boş bırakırsan şifre değişmez (en az 6 karakter yazmalısın)"
            />

            {String(form.password || '').length > 0 && String(form.password || '').length < 6 ? (
              <MDBox
                sx={{
                  p: 1.0,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.warning.main}`,
                  color: theme.palette.warning.main,
                }}
              >
                Şifre giriyorsan en az 6 karakter olmalı.
              </MDBox>
            ) : null}

            {/* ✅ Login status toggle (hesap varsa) */}
            {hasAccount ? (
              <MDBox
                sx={{
                  mt: 0.25,
                  p: 1.25,
                  borderRadius: 2,
                  border: `1px dashed ${theme.palette.divider}`,
                }}
              >
                <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                  Güvenlik
                </MDTypography>

                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  sx={{ mt: 0.75, flexWrap: 'wrap' }}
                >
                  <MDTypography
                    variant="button"
                    sx={{ color: theme.palette.text.primary, opacity: 0.95 }}
                  >
                    {isSuspended
                      ? 'Eğitmenn panel erişimi kapalı'
                      : 'Eğitmenn panel erişimini kapat'}
                  </MDTypography>

                  <MDButton
                    variant="gradient"
                    color={isSuspended ? 'success' : 'error'}
                    sx={{ borderRadius: '14px' }}
                    onClick={() => setStatusModalOpen(true)}
                    disabled={loading}
                  >
                    <Icon sx={{ mr: 0.5, fontSize: 18 }}>
                      {isSuspended ? 'check_circle' : 'lock'}
                    </Icon>
                    {isSuspended ? 'Girişi Aç' : 'Girişi Kapat'}
                  </MDButton>
                </MDBox>
              </MDBox>
            ) : null}

            {err ? (
              <MDBox
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.error.main}`,
                  color: theme.palette.error.main,
                }}
              >
                {err}
              </MDBox>
            ) : null}
          </MDBox>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2 }}>
          <MDButton
            variant="outlined"
            color="dark"
            sx={{ borderRadius: '14px' }}
            onClick={onClose}
            disabled={loading}
          >
            İptal
          </MDButton>

          <MDButton
            variant="gradient"
            color="info"
            sx={{ borderRadius: '14px' }}
            disabled={
              !canSubmit ||
              loading ||
              (String(form.password || '').length > 0 && String(form.password || '').length < 6)
            }
            onClick={submit}
          >
            <Icon sx={{ mr: 0.5, fontSize: 18 }}>save</Icon>
            Kaydet
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ✅ Tek confirm modal */}
      <ConfirmLoginStatusModal
        open={statusModalOpen}
        staff={staff}
        currentStatus={loginStatus} // "ACTIVE" | "SUSPENDED"
        loading={loading}
        onClose={() => setStatusModalOpen(false)}
        onConfirm={confirmLoginStatusChange} // (nextStatus) => Promise
      />
    </>
  );
}
